import { ContextManager, createActions, OnSignal } from 'dreamstate';

import { Callable, Maybe, Optional, Subset } from '@vimeo/core/types/generic';
import { Logger } from '@vimeo/logging';

import { liveQueryConfig } from '../../configs/LiveQueryConfig';
import { liveTrackingConfig, ILiveTrackingConfig } from '../../configs/LiveTrackingConfig';
import { updateTrackingConfig } from '../../tracking/helpers/utils';
import { EComposerApplicationType } from '../../types/composer_session';
import { IStoreInitialState } from '../../types/store';
import { getUrlQueryValue } from '../../utils/general/url_query';
import { ELiveSignal, TSafelyTrackingSignalEvent } from '../signals';

export interface ITrackingContext {
  isReady: boolean;
  config: ILiveTrackingConfig;
  trackingActions: {
    setConfig(config: Subset<ILiveTrackingConfig>): void;
    trackSafely(trackCb: Callable): void;
    setIsReady(): void;
  };
}

/**
 * Manager to take control over tracking config, ready state and deferred tracking events
 */
export class TrackingManager extends ContextManager<ITrackingContext> {
  public override context: ITrackingContext = {
    isReady: false,
    config: liveTrackingConfig,
    trackingActions: createActions({
      setConfig: (config) => this.setConfig(config),
      setIsReady: () => this.setIsReady(),
      trackSafely: (trackCb) => this.trackSafely(trackCb),
    }),
  };

  /**
   * A queue of delayed events
   */
  private trackingEventsQueue: Array<Callable> = [];

  private log: Logger = new Logger('➡TM');

  public constructor(initialState?: IStoreInitialState) {
    super();

    /**
     * For broadcaster connections let users set lead source for tracking.
     */
    if (initialState?.sessionApplicationType === EComposerApplicationType.BROADCASTER) {
      this.context.config.BIG_PICTURE_LIVE_SCHEMA_BASE.leadSource = this.getLeadSource();
    }
  }

  /**
   * Merge a partial config with the existing one.
   */
  public setConfig(config: Subset<ILiveTrackingConfig>): void {
    this.log.info('Tracking config update:', config);

    updateTrackingConfig(config, this.context.config);
    this.forceUpdate();
  }

  /**
   * Get lead source of current event.
   * Mainly used to indicate which source has led to current page.
   */
  public getLeadSource(): Optional<string> {
    return getUrlQueryValue(liveQueryConfig.UTM_CAMPAIGN) || getUrlQueryValue(liveQueryConfig.VCID);
  }

  /**
    Unleash all deferred track events
   */
  public unleashDeferredEvents(): void {
    this.trackingEventsQueue.reverse();
    while (this.trackingEventsQueue.length) {
      this.log.info('Track deferred events');
      const track: Maybe<Callable> = this.trackingEventsQueue.pop();
      if (track) {
        track();
      }
    }
  }

  /**
   * Marks that the tracking manager is ready to send track events
   */
  public setIsReady(): void {
    this.log.info('Tracking manager is ready');
    this.unleashDeferredEvents();
    this.setContext({ isReady: true });
  }

  /**
   * If the config is ready, it calls track event
   * If the config is not ready, it pushes track event to the queue
   */
  public trackSafely(trackCb: Callable): void {
    const { isReady } = this.context;

    if (isReady) {
      trackCb();
    } else {
      this.trackingEventsQueue.push(trackCb);
    }
  }

  @OnSignal(ELiveSignal.TRACK_SAFELY)
  public onTrackSafely(signalEvent: TSafelyTrackingSignalEvent): void {
    this.trackSafely(signalEvent.data);
  }
}
